import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateNotificationType,
  useDeleteNotificationType,
  useNotificationTypes,
  useUpdateNotificationType,
} from './Hooks';

const CATEGORY_OPTIONS = [
  'TRANSACTION',
  'WALLET',
  'REFUND',
  'SUPPORT',
  'CHARGER',
  'RESERVATION',
  'REFERRAL',
  'ACCOUNT',
  'REPORT',
];

const NotificationTypesTab = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useNotificationTypes();
  const createMutation = useCreateNotificationType({
    onSuccess: () => {
      message.success(t('typeSaved'));
      closeModal();
    },
    onError: (error) => message.error(error?.message || t('common:fetchError')),
  });
  const updateMutation = useUpdateNotificationType({
    onSuccess: () => {
      message.success(t('typeSaved'));
      closeModal();
    },
    onError: (error) => message.error(error?.message || t('common:fetchError')),
  });
  const deleteMutation = useDeleteNotificationType({
    onSuccess: () => message.success(t('typeDeleted')),
    onError: (error) => message.error(error?.message || t('common:fetchError')),
  });

  const rows = useMemo(() => data || [], [data]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      defaultEnabled: true,
      isActive: true,
      category: 'TRANSACTION',
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      code: record.code,
      category: record.category,
      titleEN: record.title?.EN,
      titleTR: record.title?.TR,
      bodyEN: record.body?.EN,
      bodyTR: record.body?.TR,
      defaultEnabled: record.defaultEnabled,
      isActive: record.isActive,
      iconUrl: record.iconUrl,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const body = {
      title: { EN: values.titleEN, TR: values.titleTR },
      body: { EN: values.bodyEN, TR: values.bodyTR },
      category: values.category,
      defaultEnabled: values.defaultEnabled,
      isActive: values.isActive,
      iconUrl: values.iconUrl || '',
    };

    if (editing) {
      await updateMutation.mutateAsync({ id: editing._id, body });
      return;
    }

    await createMutation.mutateAsync({
      ...body,
      code: values.code,
    });
  };

  const columns = [
    { title: t('code'), dataIndex: 'code', key: 'code' },
    { title: t('category'), dataIndex: 'category', key: 'category', render: (v) => <Tag>{v}</Tag> },
    {
      title: t('titleEN'),
      key: 'titleEN',
      render: (_, row) => row.title?.EN || '—',
    },
    {
      title: t('defaultEnabled'),
      dataIndex: 'defaultEnabled',
      key: 'defaultEnabled',
      render: (v) => (v ? t('yes') : t('no')),
    },
    {
      title: t('active'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v) => (v ? <Tag color="green">{t('yes')}</Tag> : <Tag>{t('no')}</Tag>),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            {t('edit')}
          </Button>
          <Button
            size="small"
            danger
            onClick={() =>
              Modal.confirm({
                title: t('deleteConfirmTitle'),
                content: row.code,
                onOk: () => deleteMutation.mutateAsync(row._id),
              })
            }
          >
            {t('delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="mt-4">
      <div className="flex justify-end mb-3">
        <Button type="primary" onClick={openCreate}>
          {t('addType')}
        </Button>
      </div>
      <Table
        rowKey="_id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 15 }}
        scroll={{ x: true }}
      />

      <Modal
        title={editing ? t('editType') : t('addType')}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('code')}
            name="code"
            rules={[{ required: !editing, message: t('codeRequired') }]}
          >
            <Input disabled={Boolean(editing)} placeholder="TRANSACTION_STARTED" />
          </Form.Item>
          <Form.Item label={t('category')} name="category" rules={[{ required: true }]}>
            <Select options={CATEGORY_OPTIONS.map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item label={t('titleEN')} name="titleEN" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('titleTR')} name="titleTR" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('bodyEN')} name="bodyEN" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label={t('bodyTR')} name="bodyTR" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label={t('iconUrl')} name="iconUrl">
            <Input />
          </Form.Item>
          <Form.Item label={t('defaultEnabled')} name="defaultEnabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={t('active')} name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationTypesTab;
